import { Check, Copy } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { HASH_ALGOS, type HashAlgo, hashText } from "../lib/hash";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function HashGenerator() {
  const uid = useId();
  const [input, setInput] = useState("Hello, Toolverse!");
  const [algo, setAlgo] = useState<HashAlgo>("SHA-256");
  const [hash, setHash] = useState("");
  const [copied, setCopied] = useState(false);

  // crypto.subtle.digest is async — compute in an effect (empty on SSR, fills
  // after mount → no hydration mismatch).
  useEffect(() => {
    let cancelled = false;
    hashText(input, algo)
      .then((h) => {
        if (!cancelled) setHash(h);
      })
      .catch(() => {
        if (!cancelled) setHash("");
      });
    return () => {
      cancelled = true;
    };
  }, [input, algo]);

  const copy = async () => {
    if (!hash) return;
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.hg_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.hg_placeholder()}
            spellCheck={false}
            className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5 sm:max-w-xs">
          <Label htmlFor={`${uid}-algo`}>{m.hg_algo()}</Label>
          <select
            id={`${uid}-algo`}
            className={selectClass}
            value={algo}
            onChange={(e) => setAlgo(e.target.value as HashAlgo)}
          >
            {HASH_ALGOS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.hg_output_label()}
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={copy}
            disabled={!hash}
            className="gap-2"
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? m.hg_copied() : m.hg_copy()}
          </Button>
        </div>
        <textarea
          readOnly
          data-testid="hg-output"
          aria-label={m.hg_output_label()}
          value={hash}
          spellCheck={false}
          className="min-h-[6rem] w-full resize-y break-all rounded-lg border border-border bg-background p-4 font-mono text-sm"
        />
      </div>
    </div>
  );
}
